import { Component } from '../Component'
import { element } from '../CustomElement'
import { i18n } from '../../i18n/I18n'
import { icons } from '../../icons/Icons'
import { inject } from '../../services/di'
import { isWebglEnabled } from '../../utils/webgl'
import { MIN_BROWSER_MAP } from '../../utils/browsers'
import { StorageService } from '../../services/StorageService'
import { ConfigService, RuntimeConfig } from '../../services/ConfigService'
import { AnalyticsService } from '../../services/AnalyticsService'
import { DateTimeService } from '../../services/DateTimeService'

@element('flipp-webgl-container')
export class WebGLContainer extends Component<JSX.HTMLProps, any> {
    @inject storage: StorageService
    @inject analytics: AnalyticsService
    @inject config: ConfigService
    @inject DateTimeService: DateTimeService

    prepare() {
        let hideWarning = false
        const webglEnabled = isWebglEnabled()
        const webglWarningDismissed = this.storage.getItem(
            'webglWarningDismissed'
        )
        const isOldBrowser = bowser.isUnsupportedBrowser(MIN_BROWSER_MAP)

        // If the user is using an old browser, then we'd show an 'old browser' warning.
        // We don't want to show both warnings layered on top of each other, so don't
        // show the webGl warning because in order to fix webGl, the user will most likely
        // need to upgrade their browser first.
        // ----------------------------------------------------------------------
        if (
            webglEnabled ||
            (!webglEnabled && webglWarningDismissed) ||
            isOldBrowser
        ) {
            hideWarning = true
        }

        const webglDisabledBeacon = StorageService.getInstance().getItem(
            'webgl_disabled_beacon'
        )
        let fireWebglDisabledBeacon = false
        try {
            const entry = JSON.parse(webglDisabledBeacon)
            const expired = !entry
                ? true
                : new Date().getTime() > Number(entry.expires_at)
            if (!entry || expired) {
                fireWebglDisabledBeacon = true
            }
        } catch (e) {
            fireWebglDisabledBeacon = true
        }

        if (
            !webglEnabled &&
            this.config.RUNTIME === RuntimeConfig.DISTRIBUTION
        ) {
            /* Fire the WebGL beacon every time for Distribution */
            this.analytics.trackWebGLDisabled()
        } else if (!webglEnabled && fireWebglDisabledBeacon) {
            const time = this.DateTimeService.getNow()
            StorageService.getInstance().setItem(
                'webgl_disabled_beacon',
                JSON.stringify({
                    expires_at: time.plus({ months: 1 }).valueOf(),
                })
            )

            // Fire a beacon at most once a month per user.
            // --------------------------------------------------------------------
            this.analytics.trackWebGLDisabled()
        }

        return hideWarning
    }

    render(hideWarning: boolean) {
        if (hideWarning) {
            return null
        }

        return (
            <section class="webgl-warning">
                <button
                    class="dialog-close"
                    title={i18n('close_dialog_button')}
                    aria-label={i18n('close_dialog_button')}
                    onClick={this._handleCloseButtonClick}
                >
                    {icons.searchClose}
                </button>
                <img
                    src="https://f.wishabi.net/creative/Distribution/images/sad-robot.png"
                    alt="image of a sad robot"
                />
                <div>
                    <p class="webgl-warning-msg">
                        {i18n(
                            `${this.config.RUNTIME === RuntimeConfig.PG ? 'pg_' : ''}webgl_warning_msg`
                        )}
                    </p>
                    <p class="webgl-warning-cta">{i18n('webgl_warning_cta')}</p>
                    <p class="click-here">
                        <a
                            href="https://flipp.force.com/articles/en_US/Why-do-I-see-a-grid-view-or-blank-page"
                            target="_blank"
                        >
                            {i18n('webgl_warning_click_here')}
                        </a>
                        {i18n('webgl_warning_learn')}
                    </p>
                </div>
            </section>
        )
    }

    private _handleCloseButtonClick = (e: Event) => {
        // It was decided that if WebGL is disabled, we'll show a warning and
        // allow the user to dismiss it, storing this fact in localStorage. This
        // setting will be saved until user clears its cache. In other words,
        // refreshing the page will NOT cause the warning to appear again.
        // ----------------------------------------------------------------------
        e.preventDefault()
        this.storage.setItem('webglWarningDismissed', 'true')
        this.invalidate()
    }
}
